import { Fragment, useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, ApiError } from '../api.js';
import type { GameResult, GameSummary, Standing } from '../api.js';
import { useSession } from '../App.js';
import { settingsLine } from '../components/GameSettings.js';
import { LobbyChat } from '../components/LobbyChat.js';
import { formatDuration } from '../duration.js';
import { PLAYBACK_MS } from '@roborally/engine';

export function Lobby() {
  const navigate = useNavigate();
  const { me } = useSession();
  const [games, setGames] = useState<GameSummary[] | null>(null);
  const [recent, setRecent] = useState<GameResult[]>([]);
  const [standings, setStandings] = useState<Standing[]>([]);
  const [boardUse, setBoardUse] = useState<{ board: string; expansion?: string; games: number }[]>([]);
  const [error, setError] = useState<string | null>(null);
  /** The last poll's failure, cleared by the next one that works. Kept
   * apart from `error`, which a button sets and a poll must not wipe. */
  const [pollError, setPollError] = useState<string | null>(null);
  const [removing, setRemoving] = useState<number | null>(null);
  /** The standings column the reader sorted by; null keeps the server's
   * order, most wins first. */
  const [standingsSort, setStandingsSort] = useState<{ key: StandingsKey; desc: boolean } | null>(null);

  /** The live list, which is what the poll is for. */
  const load = useCallback(async () => {
    try {
      const gameList = await api.games();
      setGames(gameList.games);
      setPollError(null);
    } catch (err) {
      setPollError(err instanceof ApiError ? err.message : 'Could not reach the server.');
    }
  }, []);

  /**
   * The history: recent games, standings and board use. Not polled with
   * the list -- it changes only when a game ends or goes, which the list
   * shows -- so it is read on opening and whenever a game leaves the list
   * or changes state. A failure leaves whatever was already shown.
   */
  const loadResults = useCallback(async () => {
    try {
      const results = await api.results();
      setRecent(results.recent);
      setStandings(results.standings);
      setBoardUse(results.boardUse ?? []);
    } catch {
      // leave whatever was already shown
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const listKey = games?.map((g) => `${g.id}:${g.status}`).join(',') ?? null;
  useEffect(() => { void loadResults(); }, [listKey, loadResults]);

  // The server sends both kinds in one list, ordered by when they were
  // last touched. Split here rather than in two requests: it is the same
  // rows, and one poll is one poll.
  const waiting = games?.filter((g) => g.status === 'lobby') ?? [];
  const running = games?.filter((g) => g.status === 'active') ?? [];

  // A stable sort over the server's order, so ties stay as it ranked them.
  const sortedStandings = standingsSort === null ? standings : [...standings].sort((a, b) => {
    const diff = standingValue(a, standingsSort.key) - standingValue(b, standingsSort.key);
    return standingsSort.desc ? -diff : diff;
  });
  // Highest first on the first click, reversed on the next.
  const sortStandings = (key: StandingsKey) => setStandingsSort((now) => (
    now?.key === key ? { key, desc: !now.desc } : { key, desc: true }
  ));
  const sortHeader = (key: StandingsKey, label: string) => {
    const active = standingsSort?.key === key;
    return (
      <th
        className="num"
        aria-sort={active ? (standingsSort.desc ? 'descending' : 'ascending') : 'none'}
      >
        <button type="button" className="sort-header" onClick={() => sortStandings(key)}>
          {label}{active ? (standingsSort.desc ? ' v' : ' ^') : ''}
        </button>
      </th>
    );
  };

  // The lobby list is the one place polling is right: nothing is happening
  // yet, so an SSE connection per idle browser would cost a worker each
  // for no benefit. The game screen uses the stream instead.
  useEffect(() => {
    const timer = setInterval(() => { void load(); }, 5000);
    return () => clearInterval(timer);
  }, [load]);

  /**
   * Deletes the GAME behind a finished row -- its course, log and roster --
   * and nothing else.
   *
   * The row goes with it, because this list is built from `games` and the
   * game is what was removed. The `game_results` row stays: it has no
   * foreign key to `games` and copies in the host and winner names
   * precisely so it can outlive one (deploy/schema.sql), so the standings
   * underneath go on counting this game. Deleting a game reclaims the
   * course and the log; it does not rewrite who has won what.
   */
  const removeFinished = async (gameId: number) => {
    if (!window.confirm(
      'Delete this game? Its course, log and roster go for good. '
      + 'It stays counted in the standings.',
    )) return;
    setRemoving(gameId);
    setError(null);
    try {
      await api.deleteGame(gameId);
    } catch (err) {
      // A 404 means the game had already gone -- a second tab, or a click
      // that landed either side of the five-second refresh. The list is
      // about to drop the row anyway, so there is nothing to report.
      if (!(err instanceof ApiError && err.status === 404)) {
        setError(err instanceof ApiError ? err.message : 'Could not delete the game.');
        setRemoving(null);
        return;
      }
    }
    // Straight away rather than waiting out the poll, so the row does not
    // sit there looking as though the click missed. A finished game is in
    // the history rather than the live list, so that is read again too.
    await Promise.all([load(), loadResults()]);
    setRemoving(null);
  };

  return (
    <div className="lobby-layout">
    <LobbyChat />
    <main className="content stack" style={{ gap: '2.5rem' }}>
      <section className="stack">
        <p className="eyebrow">New game</p>
        <div className="plate" style={{ padding: '1.25rem' }}>
          <div className="stack">
            <p className="muted">
              Choose the boards, place the flags, and set the rules.
            </p>
            <div>
              <button onClick={() => navigate('/new')}>Build a game</button>
            </div>
          </div>
        </div>
      </section>

      {/* Two lists, not one. A game taking players and a game already
          under way are different things to a reader: one is an
          invitation, the other is somewhere to go back to. They were a
          single list distinguished only by the tag at the end of the
          row. Waiting comes first -- it is the one that wants somebody
          to do something. */}
      <section className="stack">
        <div className="spread">
          <p className="eyebrow">Games awaiting players</p>
          {games && <span className="mono muted num">{waiting.length}</span>}
        </div>

        {error && <p className="notice">{error}</p>}
        {pollError && pollError !== error && <p className="notice">{pollError}</p>}

        {games === null && <p className="eyebrow">Loading</p>}

        {games !== null && waiting.length === 0 && (
          <div className="empty">
            <p className="muted">Nothing is waiting.</p>
          </div>
        )}

        <div className="stack" style={{ gap: '0.5rem' }}>
          {waiting.map((game) => (
            <GameRow
              key={game.id}
              game={game}
              onOpen={() => navigate(`/games/${game.id}`)}
            />
          ))}
        </div>
      </section>

      <section className="stack">
        <div className="spread">
          <p className="eyebrow">Games running</p>
          {games && <span className="mono muted num">{running.length}</span>}
        </div>

        {games !== null && running.length === 0 && (
          <div className="empty">
            <p className="muted">Nothing is running yet.</p>
          </div>
        )}

        <div className="stack" style={{ gap: '0.5rem' }}>
          {running.map((game) => (
            <GameRow
              key={game.id}
              game={game}
              onOpen={() => navigate(`/games/${game.id}`)}
            />
          ))}
        </div>
      </section>
      <section className="stack">
        <p className="eyebrow">Standings</p>
        {standings.length === 0 ? (
          <p className="muted">No games have been finished yet.</p>
        ) : (
          <div className="plate" style={{ padding: '0.75rem 1rem' }}>
            <table className="standings-table">
              <thead>
                <tr>
                  <th>Player</th>
                  {sortHeader('plays', 'Played')}
                  {sortHeader('wins', 'Won')}
                  {sortHeader('ratio', 'Win %')}
                  <th>Last win</th>
                </tr>
              </thead>
              <tbody>
                {sortedStandings.map((s, i) => (
                  <tr key={s.userId}>
                    <td>
                      <span className="bay standings-rank">{i + 1}</span>
                      <span className="display">{s.name}</span>
                    </td>
                    <td className="num">{s.plays}</td>
                    <td className="num">{s.wins}</td>
                    <td className="num">{Math.round(standingValue(s, 'ratio') * 100)}%</td>
                    <td className="mono muted">
                      {s.lastWin ? when(s.lastWin) : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Every game ever started counts, deleted ones included. One row
          per count, most first, with every board at that count in one
          cell, down to the boards nobody has played at 0. */}
      <section className="stack">
        <p className="eyebrow">Boards played</p>
        {boardUse.length === 0 ? (
          <p className="muted">No boards have been imported yet.</p>
        ) : (
          <div className="plate" style={{ padding: '0.75rem 1rem' }}>
            <table className="standings-table">
              <thead>
                <tr>
                  <th className="num">Games</th>
                  <th>Boards</th>
                </tr>
              </thead>
              <tbody>
                {byCount(boardUse).map(({ games, boards }) => (
                  <tr key={games}>
                    <td className="num">{games}</td>
                    <td>
                      {boards.map((b, i) => (
                        <Fragment key={b.board}>
                          {i > 0 && ', '}
                          <span className="display">{b.board}</span>
                        </Fragment>
                      ))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="stack">
        <p className="eyebrow">Finished games</p>
        {recent.length === 0 ? (
          <p className="muted">No games have been finished yet.</p>
        ) : (
          <div className="stack" style={{ gap: '0.4rem' }}>
            {recent.map((r) => (
              <div key={r.game_id} className="result-row">
                <span>
                  {/* THE COURSE NAMES THE GAME. It is what a reader scans
                      this list for -- which game was this -- and the
                      winner is a fact about the game rather than its
                      name. The winner is marked in the roster below
                      instead, beside the player who took it. */}
                  <span className="display" style={{ fontSize: '1.1rem' }}>
                    {r.course_name}
                  </span>
                  {r.board_names.length > 0 && (
                    <span className="muted">{' '}({r.board_names.join(', ')})</span>
                  )}
                  <span className="mono muted" style={{ display: 'block', fontSize: '0.75rem' }}>
                    {/* The player count leads because it is the one figure
                        every row has: each piece after it carries its own
                        separator, and a conditional first piece would
                        leave the line opening on one. */}
                    <span className="num">{r.player_count}</span> players
                    {/* 0 means the game finished before board counts were
                        recorded, so it is skipped rather than shown as a
                        course made of no boards. */}
                    {r.board_count > 0 && (
                      <>
                        {' / '}
                        <span className="num">{r.board_count}</span>
                        {r.board_count === 1 ? ' board' : ' boards'}
                      </>
                    )}
                    {/* Same treatment as the board count: 0 means the game
                        finished before flags were recorded, and is left
                        out rather than shown as a game won without
                        touching one. */}
                    {r.flag_count > 0 && (
                      <>
                        {' / '}
                        <span className="num">{r.flag_count}</span>
                        {r.flag_count === 1 ? ' flag' : ' flags'}
                      </>
                    )}
                    {' / '}
                    <span className="num">{r.turns}</span>
                    {r.turns === 1 ? ' turn' : ' turns'}
                    {/* Null on games finished before the host was
                        recorded, and left out rather than attributing the
                        game to nobody. */}
                    {r.host_user_id !== null && (
                      <>
                        {' / '}host{' '}
                        {r.host_name ?? 'user'}
                      </>
                    )}
                    {/* Three cases, not two, and the roster below can mark
                        only one of them. A null winner on a recorded game
                        is a genuine draw; a null winner on an unrecorded
                        one is a game the host abandoned, which nobody drew
                        because nobody finished it. */}
                    {!r.recorded && <>{' / '}abandoned</>}
                    {r.recorded && r.winner_user_id === null && <>{' / '}drawn</>}
                    {/* A winner who is not in `game_players` -- removed
                        from the table, or a game whose roster is gone --
                        cannot be marked in a roster that does not list
                        them, so the name is said here instead.
                        `game_results` keeps it whatever becomes of the
                        account. */}
                    {r.recorded && r.winner_user_id !== null
                      && !r.players_list.some((p) => p.userId === r.winner_user_id) && (
                      <>{' / '}won by {r.winner_name ?? 'a player who has left'}</>
                    )}
                    {' / '}{played(r.started_at, r.finished_at)}
                  </span>
                  <span className="mono muted" style={{ display: 'block', fontSize: '0.75rem' }}>
                    {settingsLine(r.settings)}
                  </span>
                  {r.players_list.length > 0 && (
                    <span className="mono muted" style={{ display: 'block', fontSize: '0.75rem' }}>
                      {/* Marked in place rather than named twice over. Only
                          on a RECORDED game: `winner_user_id` means
                          nothing on one that was abandoned. */}
                      {r.players_list
                        .map((p) => (
                          r.recorded && p.userId === r.winner_user_id
                            ? `${p.name} (winner)` : p.name
                        ))
                        .join(', ')}
                    </span>
                  )}
                </span>
                {/* Delete and Replay share one flex child so
                    `.result-row` still has exactly two, and
                    `justify-content: space-between` keeps them hard
                    right. */}
                <span style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  {/* The host, or an administrator -- matching the server,
                      which answers 403 to anyone else. A row written
                      before the host was recorded has a null
                      `host_user_id` and belongs to nobody, which is
                      exactly the case an admin is here to clear. */}
                  {me !== null && (r.host_user_id === me.userId || me.isAdmin) && (
                    <button
                      className="danger tiny"
                      onClick={() => void removeFinished(r.game_id)}
                      disabled={removing === r.game_id}
                    >
                      Delete
                    </button>
                  )}
                  {/* Offered to everyone, not just the host: the log is
                      public to anyone signed in, and watching a game back
                      takes nothing from it.

                      Not offered at all for a game that cannot be
                      replayed -- one played before the board was recorded
                      in its frames, or one nobody ever played. A button
                      that only ever answers "no" is worse than no
                      button. */}
                  {r.replayable && (
                    <button
                      className="secondary tiny"
                      onClick={() => navigate(`/games/${r.game_id}/replay`)}
                    >
                      Replay
                    </button>
                  )}
                </span>
              </div>
            ))}
          </div>
        )}
      </section>
    </main>
    </div>
  );
}

/**
 * One game in the lobby, in either list.
 *
 * The two lists show the same row: the difference between them is the
 * heading, and repeating the markup to say so would be two copies to keep
 * in step.
 */
function GameRow({ game, onOpen }: { game: GameSummary; onOpen: () => void }) {
  return (
    <button className="game-row" onClick={onOpen}>
      <span>
        {/* The game's number is how a host names a game to somebody else. */}
        <span className="display" style={{ fontSize: '1.35rem', display: 'block' }}>
          {game.course_name} <span className="muted">({game.id})</span>
        </span>
        {/* Who the table is stuck on, directly under the name and NOT
            muted: it is the one line in the row that asks the reader to
            do something. A game in the lobby has no such answer and
            shows none. */}
        {game.waiting_on && (
          <span className="mono" style={{ fontSize: '0.8125rem', display: 'block' }}>
            waiting on{' '}
            {game.waiting_on.kind === 'host'
              // Named rather than "the host", because the row is scanned
              // for a name and the reader is looking for their own.
              ? <>{game.host_name ?? 'the host'} to advance</>
              : game.waiting_on.names.join(', ')}
          </span>
        )}
        <span className="mono muted" style={{ fontSize: '0.8125rem' }}>
          <span className="num">{game.players}</span> of{' '}
          <span className="num">{game.max_players}</span>
          {' / '}
          <span className="num">{game.flag_count}</span>
          {game.flag_count === 1 ? ' flag' : ' flags'}
          {game.status === 'active' && (
            <> {' / '} turn <span className="num">{game.turn_number}</span></>
          )}
          {/* The host is who advances the table, so it is worth knowing
              at a glance which game is waiting on whom. The name is null
              once the host has left the game. */}
          {' / '}host{' '}
          {game.host_name ?? 'user'}
          {/* How old the game is, and how long since anybody moved it on. A
              game still in the lobby has taken no turns, so it says so
              rather than showing a blank field. */}
          {' / '}opened {stamp(game.created_at)}
          {' / '}
          {game.last_turn_at
            ? <>last turn {stamp(game.last_turn_at)}</>
            : 'not started'}
        </span>
        {game.players_list.length > 0 && (
          <span className="mono muted" style={{ fontSize: '0.75rem', display: 'block' }}>
            {game.players_list.map((p) => p.name).join(', ')}
          </span>
        )}
        {/* What the host set the game up with. Shown on a waiting game
            because it is what somebody decides whether to join on, and on
            a running one because a table half way through a course should
            not have to remember whether it turned debris off. */}
        <span className="mono muted" style={{ fontSize: '0.75rem', display: 'block' }}>
          {settings(game).join(' / ')}
        </span>
      </span>
      <span className={`tag ${game.status}`}>
        {game.status === 'lobby' ? 'Waiting' : 'Running'}
      </span>
    </button>
  );
}

/**
 * How the host set the game up, as the pieces of one line.
 *
 * Every setting says which way it went rather than only appearing when
 * it is on: a reader scanning for whether this table has debris is not
 * helped by a row that is silent either way. The words are the ones the
 * setup screen uses, so the two read as the same question answered.
 */
function settings(game: GameSummary): string[] {
  // The mode first: it changes how the whole game plays.
  const out: string[] = [game.turn_mode === 1 ? 'turn mode' : 'normal mode'];

  if (game.options_enabled === 0) {
    out.push('no options');
  } else {
    // The card count only where the host pared the catalog down. Null is
    // the whole of it, which is the ordinary game and needs no saying.
    const deck = game.option_count === null
      ? 'options' : `options: ${game.option_count} cards`;
    out.push(game.starting_options > 0
      ? `${deck}, ${game.starting_options} each to start`
      : deck);
  }

  out.push(game.debris_enabled === 0 ? 'no debris' : 'debris');

  out.push(game.timer_seconds === null
    ? 'no clock'
    : game.timer_mode === 'last'
      ? `${formatDuration(game.timer_seconds)} for the last player`
      : `${formatDuration(game.timer_seconds)} to program`);

  out.push(game.late_join === 0 ? 'closed to late joiners' : 'open to late joiners');

  if (game.turn_mode !== 0) out.push(game.bots_allowed === 0 ? 'no bots' : 'bots welcome');

  if (game.playback_ms !== PLAYBACK_MS) out.push(`${game.playback_ms} ms playback`);

  return out;
}

type BoardUse = { board: string; expansion?: string; games: number };

/** The standings columns a reader can sort by. */
type StandingsKey = 'plays' | 'wins' | 'ratio';

/** One standing's value in a sortable column. Number() because a MySQL
 * SUM reaches the client as a string. */
function standingValue(s: Standing, key: StandingsKey): number {
  const plays = Number(s.plays);
  const wins = Number(s.wins);
  if (key === 'plays') return plays;
  if (key === 'wins') return wins;
  return plays > 0 ? wins / plays : 0;
}

/** Boards grouped by how many games each has been played in, most first.
 * The server sends them in that order, by name within a count. */
function byCount(use: BoardUse[]): { games: number; boards: BoardUse[] }[] {
  const out: { games: number; boards: BoardUse[] }[] = [];
  for (const b of use) {
    const last = out[out.length - 1];
    if (last && last.games === b.games) last.boards.push(b);
    else out.push({ games: b.games, boards: [b] });
  }
  return out;
}

/** Dates are shown short: the exact minute of a finished game is not
 * something anyone needs, and a long timestamp crowds the row. */
function when(iso: string): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return '';
  return at.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}

/**
 * When a finished game was played, both ends of it.
 *
 * A date alone answered neither question a finished game raises -- when
 * it was played, and how long it took.
 *
 * The date is said ONCE where both ends fall on the same day, which is
 * most games; a game that ran past midnight, or was picked up again the
 * next evening, says both. `started_at` is null for a game finished
 * without ever being played, and then there is only the one end to give.
 */
function played(startIso: string | null, endIso: string): string {
  const end = new Date(endIso);
  if (Number.isNaN(end.getTime())) return '';
  if (!startIso) return when(endIso);
  const start = new Date(startIso);
  if (Number.isNaN(start.getTime())) return when(endIso);

  const clock = (at: Date) => at.toLocaleTimeString(
    undefined, { hour: '2-digit', minute: '2-digit' },
  );
  const startDay = when(startIso);
  const endDay = when(endIso);

  return startDay === endDay
    ? `${startDay} ${clock(start)} to ${clock(end)}`
    : `${startDay} ${clock(start)} to ${endDay} ${clock(end)}`;
}

/** A running game is a different case: the minute is exactly what is
 * wanted, since the question being asked of the row is how long it has
 * been sitting there. */
function stamp(iso: string): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return '';
  return at.toLocaleString(undefined, {
    day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
  });
}
